/**
 * VersichererTile — one multi-select Versicherer card (P2 Placement Slice 7, Ch 8.1). A <label>
 * card: checkbox + logo + name + #rang, the Eignung chip, the family Kennzahl (+ the exact-leaf
 * secondary line), the «zuletzt platziert» line, the gesamt line, an amber «keine E-Mail» warning,
 * the «Offertanfrage erstellt» lock, and a «Versicherer öffnen» link to the carrier detail.
 */

import { useNavigate } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { EntityLink } from '../../components/ui/EntityLink';
import { monthYearDe } from '../../lib/format';
import { VERSICHERER_EIGNUNG, VERSICHERER_KEINE_EMAIL_HINWEIS } from '@brokly/shared';
import type { VersichererRankingEintrag } from '@brokly/shared';
import { CarrierLogo } from './CarrierLogo';

export function VersichererTile({
  eintrag,
  checked,
  disabled,
  onToggle,
  familieLabel,
  sparteLabel,
  sparteIsLeaf,
}: {
  eintrag: VersichererRankingEintrag;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
  familieLabel: string;
  sparteLabel: string;
  sparteIsLeaf: boolean;
}) {
  const navigate = useNavigate();
  const displayName = eintrag.name ?? eintrag.carrier_uid;
  const isGeeignet = eintrag.eignung === VERSICHERER_EIGNUNG.GEEIGNET;
  const letzte = eintrag.familie.letzte_platzierung_am;

  return (
    <label
      className="flex cursor-pointer flex-col gap-2 rounded-lg border border-border bg-card p-3 text-sm has-[:checked]:border-primary has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-70"
      data-testid={`vt-tile-${eintrag.carrier_uid}`}
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          className="mt-1"
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
          aria-label={`${displayName} auswählen`}
          data-testid={`vt-check-${eintrag.carrier_uid}`}
        />
        <CarrierLogo name={eintrag.name} logoUrl={eintrag.logo_url} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className={eintrag.name ? 'font-medium' : 'font-medium mono-num'}>{displayName}</span>
            <span className="shrink-0 text-xs text-muted-foreground">#{eintrag.rang}</span>
          </div>
          <Badge
            variant={isGeeignet ? 'success' : 'neutral'}
            className="mt-1"
            title={eintrag.eignung_gruende.join(' · ')}
          >
            {isGeeignet ? 'Geeignet' : 'Keine Historie'}
          </Badge>
        </div>
      </div>

      <div className="flex flex-col gap-0.5 text-xs">
        <span>
          <strong className="text-sm">{eintrag.familie.anzahl_policen_aktiv}</strong> aktive Policen {familieLabel}
        </span>
        {sparteIsLeaf && eintrag.sparte.anzahl_policen_aktiv > 0 && (
          <span className="text-muted-foreground">
            davon {eintrag.sparte.anzahl_policen_aktiv} {sparteLabel}
          </span>
        )}
        <span className="text-muted-foreground">
          {letzte ? `zuletzt platziert ${monthYearDe(letzte)}` : 'noch nie platziert'}
        </span>
        <span className="text-muted-foreground">{eintrag.gesamt.anzahl_policen_total} Policen gesamt</span>
      </div>

      {!eintrag.submission_faehig && (
        <p role="note" className="text-xs text-status-warning">
          {VERSICHERER_KEINE_EMAIL_HINWEIS}
        </p>
      )}
      {eintrag.auswahl_gesperrt && (
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          <Lock className="size-3" /> Offertanfrage erstellt
        </p>
      )}

      <EntityLink
        onActivate={() => void navigate({ to: '/versicherer/$id', params: { id: eintrag.carrier_uid } })}
      >
        Versicherer öffnen
      </EntityLink>
    </label>
  );
}
